import { type ReactNode, useCallback, useState } from 'react';

import { BodyTextSmall, HeadingS, IconChevronRight } from 'lib/design-system';

import {
  FunnellingBaseButton,
  ButtonText,
  ButtonLabel,
  SubHeading,
  Description,
  SelectionButtonContainer,
} from '../styles';
import { neutral600, spacing3, default500 } from 'lib/mortar';
import styled from 'styled-components';
import { Heading } from '@checkatrade/web-ui';
import Radio from 'lib/mortar/components/fields/radio';

export const SelectionButton = ({
  text,
  isSelected,
  onSelect,
  children,
}: {
  isSelected?: boolean;
  text: string;
  onSelect: () => void;
  children?: ReactNode;
}) => (
  <FunnellingBaseButton isSelected={isSelected} onClick={onSelect}>
    <ButtonLabel>
      <ButtonText>{text}</ButtonText>
      {children}
    </ButtonLabel>

    <div className="ml-auto flex items-center">
      <IconChevronRight size="small" color={default500} />
    </div>
  </FunnellingBaseButton>
);

export type BaseChoice = {
  title: string;
  value: string | number;
  subheading?: string;
  description?: string;
  selected?: boolean;
};

const TextSmall = styled(BodyTextSmall)`
  color: ${neutral600};
`;

const toLabel = (choice: BaseChoice) => ({
  label: choice.title,
  subheading: choice.subheading,
  value: String(choice.value),
});

export const SelectScreen = <C extends BaseChoice>({
  title,
  description,
  choices,
  showSubheading = false,
  showDescription = false,
  radioButtons = false,
  onSelect,
  onNext,
  desktopView = false,
}: {
  title: string;
  description?: string;
  choices: C[];
  showSubheading?: boolean;
  showDescription?: boolean;
  radioButtons?: boolean;
  onSelect?: (choice: C) => void;
  onNext?: () => void;
  desktopView?: boolean;
}) => {
  const [optionSelected, setOptionSelected] = useState(choices[0]);
  const onChoiceSelect = (choice: C) => {
    onSelect && onSelect(choice);
    onNext && onNext();
  };

  const getOptionFromValue = useCallback(
    (optionValue: string) =>
      choices.find(choice => choice?.value === optionValue) || choices[0],
    [choices],
  );

  return (
    <section>
      {desktopView ? (
        <>
          <Heading
            as="h3"
            variant="headingSM"
            className="mb-2 text-2xl font-bold text-secondary"
          >
            {title}
          </Heading>
          <TextSmall style={{ margin: `0 0 ${spacing3} 0` }}>
            We&apos;ll match you with specialist tradespeople
          </TextSmall>
        </>
      ) : (
        <HeadingS as="h2" style={{ marginBottom: spacing3 }}>
          {title}
        </HeadingS>
      )}
      {description && (
        <Description style={{ margin: `0 0 ${spacing3} 0` }}>
          {description}
        </Description>
      )}
      {desktopView && (
        <SelectionButtonContainer>
          {choices.map(choice => (
            <SelectionButton
              key={choice.value}
              text={choice.title}
              onSelect={() => onChoiceSelect(choice)}
              isSelected={choice.selected}
            >
              {showSubheading && choice.subheading && (
                <SubHeading>{choice.subheading}</SubHeading>
              )}
              {showDescription && choice.description && (
                <Description>{choice.description}</Description>
              )}
            </SelectionButton>
          ))}
        </SelectionButtonContainer>
      )}

      {!desktopView && (
        <SelectionButtonContainer>
          {radioButtons && (
            <Radio
              name="preferredStart"
              onChange={e => {
                const selected = getOptionFromValue(e.target.value);
                setOptionSelected(selected);
                onChoiceSelect(selected);
              }}
              value={String(optionSelected?.value)}
              options={choices.map(toLabel)}
              customContainerStyle={{ margin: 0, padding: '4px 0 4px 16px' }}
            />
          )}
          {!radioButtons
            ? choices.map(choice => (
                <SelectionButton
                  key={choice.value}
                  text={choice.title}
                  onSelect={() => onChoiceSelect(choice)}
                  isSelected={choice.selected}
                >
                  {showSubheading && choice.subheading && (
                    <SubHeading>{choice.subheading}</SubHeading>
                  )}
                  {showDescription && choice.description && (
                    <Description>{choice.description}</Description>
                  )}
                </SelectionButton>
              ))
            : null}
        </SelectionButtonContainer>
      )}
    </section>
  );
};
